import React, { useEffect, useState } from "react";

export interface ErrorDetails {
    title: string;
    message?: string;
    code?: string;
    id?: string;
}

interface ErrorNotificationProps {
    error: ErrorDetails;
    onClose: () => void;
    duration?: number;
}

export function ErrorNotification({ error, onClose, duration = 30000 }: ErrorNotificationProps) {
    const [isVisible, setIsVisible] = useState(true);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [duration]);

    const handleClose = () => {
        setIsLeaving(true);
        setTimeout(() => {
            setIsVisible(false);
            onClose();
        }, 300);
    };

    if (!isVisible) return null;

    return (
        <div
            className={`bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 ${
                isLeaving ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
            }`}
            style={{ minWidth: '50rem' }}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
        >
            {/* Header */}
            <div className="bg-red-500 px-4 py-2 flex items-center justify-between">
                <strong className="text-gray-900 font-bold">Error</strong>
                <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-900 hover:text-gray-700 transition-colors"
                    aria-label="Close"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>

            {/* Body */}
            <div className="p-4">
                <p className="mb-2 text-gray-900">{error.title}</p>
                {(error.message || error.code || error.id) && (
                    <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto whitespace-pre-wrap">
                        <code className="text-gray-800">
                            {error.message}
                            {error.code && `\n\nCode: ${error.code}`}
                            {error.id && `\nError id: ${error.id}`}
                        </code>
                    </pre>
                )}
            </div>
        </div>
    );
}

interface ErrorNotificationContainerProps {
    errors: Array<ErrorDetails & { key: string }>;
    onRemove: (key: string) => void;
}

export function ErrorNotificationContainer({ errors, onRemove }: ErrorNotificationContainerProps) {
    return (
        <div
            id="notificationPanel"
            className="fixed top-4 right-4 z-50 flex flex-col gap-3 max-w-full"
            style={{ pointerEvents: 'none' }}
        >
            {errors.map((error) => (
                <div key={error.key} style={{ pointerEvents: 'auto' }}>
                    <ErrorNotification
                        error={error}
                        onClose={() => onRemove(error.key)}
                    />
                </div>
            ))}
        </div>
    );
}

